import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../../../components/ui/dialog"
import { Visit } from '../types/visit'
import { Button } from "../../../components/ui/button"
import { Printer } from 'lucide-react'
import { format } from 'date-fns'
import { useClinicSettings } from '../../../lib/store/clinic-settings'
import { useUser } from '../../../features/settings/hooks/useUser'

interface PrintVisitDialogProps {
  visit: Visit
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function PrintVisitDialog({ visit, open, onOpenChange }: PrintVisitDialogProps) {
  const { clinicName } = useClinicSettings()
  const { data: physician, isLoading: isLoadingPhysician } = useUser(visit.doctorId)
  
  const handlePrint = () => {
    window.print()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl">
        <DialogHeader className="print:hidden">
          <DialogTitle>Print Visit Record</DialogTitle>
          <Button onClick={handlePrint} className="absolute right-4 top-4">
            <Printer className="h-4 w-4 mr-2" />
            Print
          </Button>
        </DialogHeader>

        <div className="p-6 space-y-6 bg-background-light-base text-background-light-primary print:bg-white print:text-black dark:bg-background-dark-base dark:text-background-dark-primary print:dark:bg-white print:dark:text-black">
          <div className="text-center mb-8">
            {clinicName && (
              <h1 className="text-3xl font-bold mb-4 print:text-black">{clinicName}</h1>
            )}
            <h2 className="text-2xl font-bold print:text-black">Visit Record</h2>
            <p className="text-text-light-secondary dark:text-text-dark-secondary print:text-gray-600">
              Generated on {format(new Date(), 'MMMM d, yyyy')}
            </p>
          </div>

          <div className="space-y-6">
            <section>
              <h2 className="text-xl font-semibold mb-4 print:text-black">Visit Information</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="font-medium print:text-black">Patient</p>
                  <p className="print:text-black">{visit.title}</p>
                </div>
                <div>
                  <p className="font-medium print:text-black">Date & Time</p>
                  <p className="print:text-black">{format(new Date(visit.startTime), 'MMMM d, yyyy h:mm a')}</p>
                </div>
                <div>
                  <p className="font-medium print:text-black">Visit Type</p>
                  <p className="capitalize print:text-black">{visit.type}</p>
                </div>
                <div>
                  <p className="font-medium print:text-black">Status</p>
                  <p className="capitalize print:text-black">{visit.status}</p>
                </div>
                <div>
                  <p className="font-medium print:text-black">Physician</p>
                  {isLoadingPhysician ? (
                    <p className="print:text-black">Loading physician details...</p>
                  ) : physician ? (
                    <p className="print:text-black">
                      {physician.title} {physician.firstName} {physician.lastName}
                      {physician.specialization && (
                        <span className="block text-sm print:text-gray-600">
                          {physician.specialization}
                        </span>
                      )}
                    </p>
                  ) : (
                    <p className="print:text-black">Physician details not found</p>
                  )}
                </div>
              </div>
            </section>

            {visit.vitals && (
              <section>
                <h2 className="text-xl font-semibold mb-4 print:text-black">Vitals</h2>
                <div className="grid grid-cols-2 gap-4">
                  {visit.vitals.temperature && (
                    <div>
                      <p className="font-medium print:text-black">Temperature</p>
                      <p className="print:text-black">{visit.vitals.temperature}°C</p>
                    </div>
                  )}
                  {visit.vitals.bloodPressure && (
                    <div>
                      <p className="font-medium print:text-black">Blood Pressure</p>
                      <p className="print:text-black">{visit.vitals.bloodPressure}</p>
                    </div>
                  )}
                  {visit.vitals.heartRate && (
                    <div>
                      <p className="font-medium print:text-black">Heart Rate</p>
                      <p className="print:text-black">{visit.vitals.heartRate} bpm</p>
                    </div>
                  )}
                  {visit.vitals.respiratoryRate && (
                    <div>
                      <p className="font-medium print:text-black">Respiratory Rate</p>
                      <p className="print:text-black">{visit.vitals.respiratoryRate} breaths/min</p>
                    </div>
                  )}
                  {visit.vitals.oxygenSaturation && (
                    <div>
                      <p className="font-medium print:text-black">Oxygen Saturation</p>
                      <p className="print:text-black">{visit.vitals.oxygenSaturation}%</p>
                    </div>
                  )}
                  {visit.vitals.height && (
                    <div>
                      <p className="font-medium print:text-black">Height</p>
                      <p className="print:text-black">{visit.vitals.height} m</p>
                    </div>
                  )}
                  {visit.vitals.weight && (
                    <div>
                      <p className="font-medium print:text-black">Weight</p>
                      <p className="print:text-black">{visit.vitals.weight} kg</p>
                    </div>
                  )}
                  {visit.vitals.bmi && (
                    <div>
                      <p className="font-medium print:text-black">BMI</p>
                      <p className="print:text-black">{visit.vitals.bmi}</p>
                    </div>
                  )}
                  {visit.vitals.bloodSugar && (
                    <div>
                      <p className="font-medium print:text-black">Blood Sugar</p>
                      <p className="print:text-black">{visit.vitals.bloodSugar} mmol/L</p>
                    </div>
                  )}
                </div>
              </section>
            )}

            {visit.symptoms && visit.symptoms.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-4 print:text-black">Symptoms</h2>
                <ul className="list-disc list-inside print:text-black">
                  {visit.symptoms.map((symptom, index) => (
                    <li key={index}>{symptom}</li>
                  ))}
                </ul>
              </section>
            )}

            {visit.diagnosis && (
              <section>
                <h2 className="text-xl font-semibold mb-4 print:text-black">Diagnosis</h2>
                <p className="whitespace-pre-wrap print:text-black">{visit.diagnosis}</p>
              </section>
            )}

            {visit.prescriptions && visit.prescriptions.length > 0 && (
              <section>
                <h2 className="text-xl font-semibold mb-4 print:text-black">Prescriptions</h2>
                <div className="space-y-4">
                  {visit.prescriptions.map((prescription, index) => (
                    <div key={index} className="border-b pb-4 last:border-b-0 last:pb-0">
                      <div className="grid gap-2">
                        <div>
                          <p className="font-medium print:text-black">{prescription.medication}</p>
                          <p className="text-sm text-text-light-secondary dark:text-text-dark-secondary print:text-gray-500">
                            {prescription.dosage} - {prescription.frequency} for {prescription.duration}
                          </p>
                        </div>
                        {prescription.notes && (
                          <p className="text-sm text-text-light-accent dark:text-text-dark-accent print:text-gray-600">
                            {prescription.notes}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {visit.notes && (
              <section>
                <h2 className="text-xl font-semibold mb-4 print:text-black">Notes</h2>
                <p className="whitespace-pre-wrap print:text-black">{visit.notes}</p>
              </section>
            )}
          </div>

          <div className="text-center text-sm text-text-light-accent dark:text-text-dark-accent print:text-gray-500 mt-8">
            This document is confidential and protected by HIPAA regulations.
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
